export class HostTokenManager {
    constructor(options) {
        Object.defineProperty(this, "storage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "storageKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "refreshThreshold", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "getTokenCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "inFlight", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "debugLogging", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.storage = options.storage;
        this.storageKey = options.storageKey ?? 'hostAccessToken';
        this.refreshThreshold = options.refreshThreshold;
        this.getTokenCallback = options.getToken;
        this.onError = options.onError;
        this.debugLogging = options.debugLogging ?? false;
    }
    debugLog(message, ...args) {
        if (this.debugLogging) {
            console.log(`HostTokenManager: ${message}`, ...args);
        }
    }
    debugWarn(message, ...args) {
        if (this.debugLogging) {
            console.warn(`HostTokenManager: ${message}`, ...args);
        }
    }
    async getToken() {
        const cached = this.storage.getItem(this.storageKey);
        if (cached && !(await this.isNearExpiry(cached))) {
            const info = this.getExpiryInfo(cached);
            this.debugLog('using cached host token', info
                ? {
                    exp: new Date(info.exp * 1000).toISOString(),
                    expiresInSec: info.secondsLeft,
                }
                : '(no exp)');
            return cached;
        }
        if (!this.inFlight) {
            this.inFlight = (async () => {
                try {
                    this.debugLog('refreshing host token (near expiry or missing)');
                    const fresh = await this.getTokenCallback();
                    if (fresh) {
                        this.storage.setItem(this.storageKey, fresh);
                        const info = this.getExpiryInfo(fresh);
                        this.debugLog('refreshed host token and cached', info
                            ? {
                                exp: new Date(info.exp * 1000).toISOString(),
                                expiresInSec: info.secondsLeft,
                            }
                            : '(no exp)');
                        return fresh;
                    }
                    this.debugWarn('host getTokenCallback returned empty token');
                    return null;
                }
                catch (error) {
                    console.error('HostTokenManager: token refresh error', error);
                    if (this.onError)
                        this.onError(error);
                    throw error;
                }
            })().finally(() => {
                this.inFlight = undefined;
            });
        }
        return this.inFlight;
    }
    clear() {
        try {
            this.storage.removeItem(this.storageKey);
        }
        catch {
            // ignore
        }
    }
    async isNearExpiry(token) {
        try {
            const payload = this.decodeJwt(token);
            if (!payload?.exp)
                return true;
            const now = Math.floor(Date.now() / 1000);
            const isNear = payload.exp <= now + this.refreshThreshold;
            if (isNear) {
                this.debugLog('token near expiry - will refresh');
            }
            return isNear;
        }
        catch (error) {
            this.debugWarn('failed to decode JWT', error);
            return true;
        }
    }
    decodeJwt(token) {
        const parts = token.split('.');
        if (parts.length < 2)
            throw new Error('Invalid JWT');
        const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = typeof atob === 'function'
            ? atob(b64)
            : Buffer.from(b64, 'base64').toString('utf8');
        return JSON.parse(json);
    }
    getExpiryInfo(token) {
        try {
            const payload = this.decodeJwt(token);
            if (!payload?.exp)
                return null;
            const now = Math.floor(Date.now() / 1000);
            return { exp: payload.exp, secondsLeft: Math.max(0, payload.exp - now) };
        }
        catch {
            return null;
        }
    }
}
//# sourceMappingURL=hostTokenManager.js.map